import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { isStaffRole, type Permission } from '@aforium/shared';
import { useAuth } from '../providers/AuthProvider';
import { Spinner } from '../components/ui/Spinner';

/** Full-screen wait while the initial silent refresh resolves. */
function AuthLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas">
      <Spinner size="lg" />
      <span className="sr-only">Loading your session…</span>
    </div>
  );
}

/** Requires a signed-in user; sends anonymous visitors to /login with a return path. */
export function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <AuthLoading />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
}

/**
 * Admin/mentor only. An intern who lands here is sent to their own dashboard
 * rather than shown a 403 — the administration area should not exist for them
 * (spec section 3).
 */
export function StaffRoute() {
  const { user, isLoading } = useAuth();

  if (isLoading) return <AuthLoading />;

  if (!user || !isStaffRole(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

/** Interns only — keeps staff out of the learner-only surfaces. */
export function InternRoute() {
  const { user, isLoading } = useAuth();

  if (isLoading) return <AuthLoading />;

  if (!user) return <Navigate to="/login" replace />;
  if (isStaffRole(user.role)) return <Navigate to="/admin" replace />;

  return <Outlet />;
}

/** Guards a branch behind one capability. */
export function PermissionRoute({ permission }: { permission: Permission }) {
  const { can, isLoading } = useAuth();

  if (isLoading) return <AuthLoading />;
  if (!can(permission)) return <Navigate to="/" replace />;

  return <Outlet />;
}

/** Signed-in users should never see the login screen. */
export function PublicOnlyRoute() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return <AuthLoading />;

  if (isAuthenticated && user) {
    return <Navigate to={isStaffRole(user.role) ? '/admin' : '/'} replace />;
  }

  return <Outlet />;
}
